// ─── L'esito di ogni azione (26/09/2026) ────────────────────────────────────
//
// 🗣️ Dal committente: *«quando faccio un'azione di conferma sul gestionale, ci
// deve essere un avviso se è andata a buon fine o no … Qualsiasi tipo di
// operazione»* (mockup approvato, «Ogni azione dice com'è andata»).
//
// ⭐ UN POSTO SOLO. L'avviso non lo scrive la pagina: lo scrive il
// `MutationCache` di `main.tsx`, che vede passare ogni `useMutation`. Una
// pagina nuova non può dimenticarselo; deve solo dare il nome all'azione
// (`meta.fatto`), e una prova (`esiti.test.ts`) guarda che ce l'abbia.
//
// ⚖️ GLI ESITI SONO TRE, NON DUE (la stessa regola del bot):
//   ✅ fatto       — il gestionale ha detto sì;
//   ❌ non fatto   — il gestionale ha detto no, e dice perché;
//   ⚠️ non so      — la risposta non è arrivata (linea caduta, server che si
//                    riavvia, risposta illeggibile). L'azione può essere
//                    riuscita lo stesso: chi la rifà a occhi chiusi rischia una
//                    prenotazione o un incasso doppio. Si dice di CONTROLLARE,
//                    mai di riprovare.

import { ErroreApi } from './api.js';

/** Cosa sa un'azione di sé, per dire com'è andata. */
export type MetaDellAzione = {
  /**
   * La frase del ✅, dopo «Fatto:» — minuscola, senza punto: «prenotazione
   * annullata». Una funzione può tacere (`null`): un'anteprima o una prova non
   * cambiano niente, e un ✅ lì direbbe il falso. Può anche dare un avviso
   * intero, quando la risposta dice «in parte» (`perGiorni`).
   */
  fatto?: string | ((dati: unknown, variabili: unknown) => string | null | Omit<Esito, 'id'>);
  /**
   * I rifiuti che sono una DOMANDA sullo schermo, non un esito: la pagina li
   * trasforma in una scelta (per esempio «CHI_LO_CHIEDE»). Un ❌ sopra
   * la domanda direbbe «non fatto» a chi deve solo scegliere.
   */
  domande?: readonly string[];
};

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MetaDellAzione;
  }
}

export type TipoEsito = 'ok' | 'no' | 'forse';
export type Esito = { id: number; tipo: TipoEsito; testo: string; sotto: string | null };

/** ⏱️ Il ✅ se ne va da solo; ❌ e ⚠️ restano finché qualcuno li chiude. */
export const DURATA_OK_MS = 4000;
/** Oltre tre, i più vecchi lasciano il posto: una pila che copre la pagina non aiuta. */
const MASSIMO = 3;

export const CONTROLLA_PRIMA = 'Prima di rifarla, aggiorna la pagina e controlla.';

/**
 * Da un errore, l'esito da dire.
 *
 * ⚖️ Un rifiuto con la sua frase (4xx) è un «no» sicuro: il gestionale ha
 * letto la richiesta e non l'ha fatta. Tutto il resto — la linea che cade
 * (`TypeError`), un 5xx, una risposta che non si legge — è un «non so».
 */
export function esitoDelGuasto(errore: unknown): Omit<Esito, 'id'> {
  if (errore instanceof ErroreApi && errore.stato < 500 && errore.codice !== 'RISPOSTA_ILLEGGIBILE') {
    return { tipo: 'no', testo: `Non fatto: ${minuscola(errore.message)}`, sotto: null };
  }
  const perche = errore instanceof ErroreApi ? 'il gestionale non ha risposto' : 'la linea si è interrotta';
  return { tipo: 'forse', testo: `Non so se è andata: ${perche}.`, sotto: CONTROLLA_PRIMA };
}

/** «Esiste già…» ⇒ «esiste già…», dopo i due punti. Una sigla («SMTP…») resta com’è. */
function minuscola(frase: string): string {
  return /^\p{Lu}\p{Ll}/u.test(frase) ? frase[0]!.toLowerCase() + frase.slice(1) : frase;
}

/** L'avviso di un'azione andata, o `null` se l'azione ha scelto di tacere. */
export function esitoRiuscito(
  meta: MetaDellAzione | undefined,
  dati: unknown,
  variabili: unknown,
): Omit<Esito, 'id'> | null {
  const f = meta?.fatto;
  const detto = typeof f === 'function' ? f(dati, variabili) : (f ?? 'operazione riuscita');
  if (detto === null) return null;
  return typeof detto === 'string' ? { tipo: 'ok', testo: `Fatto: ${detto}`, sotto: null } : detto;
}

/**
 * ⚖️ Un gesto su più giorni (una fascia, una riga del listino, un orario del
 * maestro) va avanti giorno per giorno, e un giorno rifiutato non ferma gli
 * altri. Allora «fatto» vale solo se sono passati tutti: con un rifiuto, anche
 * uno, l'avviso è rosso e dice quanti, e il motivo sta accanto al suo giorno.
 */
export function perGiorni(esiti: ReadonlyArray<{ errore: string | null }>, cosa: string): Omit<Esito, 'id'> {
  const no = esiti.filter((e) => e.errore !== null).length;
  const si = esiti.length - no;
  const giorni = (n: number) => (n === 1 ? '1 giorno' : `${n} giorni`);
  if (no === 0) return { tipo: 'ok', testo: `Fatto: ${cosa} su ${giorni(si)}`, sotto: null };
  return {
    tipo: 'no',
    testo:
      si === 0
        ? `Non fatto: ${cosa} rifiutata su ${giorni(no)}`
        : `Fatto in parte: ${cosa} su ${giorni(si)}, rifiutata su ${giorni(no)}`,
    sotto: 'Il motivo è scritto accanto a ogni giorno.',
  };
}

/** Il rifiuto è una domanda che la pagina fa da sé? */
export function eUnaDomanda(meta: MetaDellAzione | undefined, errore: unknown): boolean {
  return errore instanceof ErroreApi && (meta?.domande ?? []).includes(errore.codice);
}

// ── la pila degli avvisi ─────────────────────────────────────────────────────

let pila: Esito[] = [];
let prossimo = 1;
const ascoltatori = new Set<() => void>();
const annuncia = () => ascoltatori.forEach((a) => a());

export function ascoltaEsiti(a: () => void): () => void {
  ascoltatori.add(a);
  return () => ascoltatori.delete(a);
}

export const esitiAdesso = (): readonly Esito[] => pila;

export function mostraEsito(e: Omit<Esito, 'id'>): number {
  const id = prossimo++;
  pila = [...pila, { ...e, id }].slice(-MASSIMO);
  annuncia();
  if (e.tipo === 'ok') setTimeout(() => chiudiEsito(id), DURATA_OK_MS);
  return id;
}

export function chiudiEsito(id: number): void {
  const dopo = pila.filter((e) => e.id !== id);
  if (dopo.length === pila.length) return;
  pila = dopo;
  annuncia();
}

/** L'euro dall'importo in centesimi, per le frasi degli avvisi. */
export function inEuro(cents: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}
